// services/google-places-service/google-places.service.js
import axios from 'axios';

const GOOGLE_PLACES_API_KEY = process.env.VUE_APP_GOOGLE_PLACES_API_KEY;
const NEW_BASE_URL = 'https://places.googleapis.com/v1';

export default {
  /**
   * Find city / address / postcode from an address using Places API (New).
   * @param {Object} params - { address1, address2, postcode, country, bankName, city }
   * @param {Object} missing - { city: bool, address: bool, postcode: bool }
   * @returns {Promise<Object|null>} - { city?, address?, postcode? } or null
   */
  async findCityFromAddress({ address1, address2, postcode, country, bankName, city }, missing = {}) {
    try {
      const query = [bankName, address1, address2, city, postcode, country].filter(Boolean).join(', ');

      if (!query.trim()) return null;

      const response = await axios.post(
        `${NEW_BASE_URL}/places:searchText`,
        {
          textQuery: query
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
            'X-Goog-FieldMask': 'places.addressComponents,places.formattedAddress'
          }
        }
      );

      const place = response.data?.places?.[0];
      if (!place) return null;

      const components = place.addressComponents || [];

      const pick = (types) => components.find((c) => c.types.some((t) => types.includes(t)))?.longText || '';

      const locality =
        pick(['locality']) ||
        pick(['postal_town']) ||
        pick(['administrative_area_level_2']) ||
        pick(['administrative_area_level_1']);

      // ✅ NEW: Field is 'postalCode' in camelCase
      const postalCode = pick(['postal_code']);

      const streetNumber = pick(['street_number']);
      const route = pick(['route']);
      const subpremise = pick(['subpremise']);
      const premise = pick(['premise']);

      const streetAddress = [subpremise || premise, streetNumber, route].filter(Boolean).join(' ').trim();

      const wantsAll = !missing || Object.keys(missing).length === 0;

      const result = {};
      if (wantsAll || missing.city) result.city = locality || '';
      if (wantsAll || missing.address) result.address = streetAddress || '';
      if (wantsAll || missing.postcode) result.postcode = postalCode || '';

      const hasAny = Object.values(result).some((v) => v && v.trim());
      return hasAny ? result : null;
    } catch (err) {
      // Log the full error for better debugging
      console.error('Places API (New) error:', err.response?.data || err.message);
      return null;
    }
  }
};
